import {useCallback} from 'react';
import {useMutation} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {deleteFromCountries} from '../store/slice';
import deleteCountryMutation from '../graphql/deleteCountryMutation';
import {Country} from '../store/types';

interface UseDeleteCountryResult {
  deleteCountry: (
    countryId: string,
    onSuccess?: (country: Country) => void,
    onFailure?: (errorMessage: string) => void
  ) => Promise<void>;
}

export const useDeleteCountry = (): UseDeleteCountryResult => {
  const dispatch = useAppDispatch();

  const [mutate] = useMutation<{deleteCountry: Country}>(
    deleteCountryMutation,
    {
      errorPolicy: 'all'
    }
  );

  const deleteCountry = useCallback(
    async (
      countryId: string,
      onSuccess?: (country: Country) => void,
      onFailure?: (errorMessage: string) => void
    ) => {
      try {
        const {data, errors} = await mutate({
          variables: {id: countryId}
        });

        if (errors && errors.length > 0) {
          const errorMessage =
            errors[0]?.extensions?.errorMessage ?? errors[0].message;
          console.error('Delete country error:', errorMessage);
          onFailure?.(errorMessage as string);
          return;
        }

        if (data?.deleteCountry) {
          dispatch(deleteFromCountries(data.deleteCountry));
          onSuccess?.(data.deleteCountry);
        } else {
          const errorMessage = 'No data returned from deleteCountry mutation';
          console.error(errorMessage);
          onFailure?.(errorMessage);
        }
      } catch (error) {
        const errorMessage = `Unexpected error while deleting country: ${error}`;
        console.error(errorMessage);
        onFailure?.(errorMessage);
      }
    },
    [dispatch, mutate]
  );

  return {deleteCountry};
};
